import { useEffect, useRef, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Camera, Trash2 } from "lucide-react"

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { csrfToken } from "@/lib/orderActions"
import { Avatar } from "@/components/tuw/Avatar"

// A labeled field. `id` doubles as the form-state key, which is the exact PATCH
// param name (R5), so no translation layer sits between the form and the server.
function Field({ id, label, value, onChange, ...rest }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value ?? ""} onChange={(e) => onChange(id, e.target.value)} {...rest} />
    </div>
  )
}

// The Profile edit modal. Seeded directly from the presenter's `editable` block,
// whose keys ARE the PATCH param names, so `form` maps 1:1 to
// Customers::ProfileUpdater's params. Username + Email render as disabled context
// rows and are never submitted (D4). Timezone is NOT edited here — Settings owns
// the one canonical zone editor, so this modal only links out to it.
const AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"]
const MAX_AVATAR_BYTES = 5 * 1024 * 1024

export default function EditProfileModal({ open, onOpenChange, identity, editable, onAvatarChange }) {
  const [form, setForm] = useState(editable)
  const [saving, setSaving] = useState(false)
  const [avatarSaving, setAvatarSaving] = useState(false)
  const [avatarError, setAvatarError] = useState("")
  const fileInputRef = useRef(null)

  // Re-seed whenever a fresh editable block arrives (after a save re-renders the
  // page) or the modal reopens, so stale form state never lingers.
  useEffect(() => {
    if (open) {
      setForm(editable)
      setAvatarError("")
    }
  }, [open, editable])

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  function submit(event) {
    event.preventDefault()
    setSaving(true)
    router.patch("/profile", form, {
      headers: { "X-CSRF-Token": csrfToken() },
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
      onFinish: () => setSaving(false),
    })
  }

  async function uploadAvatar(event) {
    const file = event.target.files?.[0]
    event.target.value = ""
    if (!file) return

    if (!AVATAR_TYPES.includes(file.type) || file.size === 0 || file.size > MAX_AVATAR_BYTES) {
      setAvatarError("Choose a JPEG, PNG, or WebP image no larger than 5 MB.")
      return
    }

    const body = new FormData()
    body.append("avatar", file)
    setAvatarSaving(true)
    setAvatarError("")

    try {
      const response = await fetch("/profile/avatar", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
        body,
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error)
      onAvatarChange?.(payload.avatarUrl)
    } catch (error) {
      setAvatarError(error.message || "We couldn't upload that image. Please try again.")
    } finally {
      setAvatarSaving(false)
    }
  }

  async function removeAvatar() {
    setAvatarSaving(true)
    setAvatarError("")
    try {
      const response = await fetch("/profile/avatar", {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
      })
      const payload = await response.json()
      if (!response.ok) throw new Error(payload.error)
      onAvatarChange?.(null)
    } catch (error) {
      setAvatarError(error.message || "We couldn't remove that image. Please try again.")
    } finally {
      setAvatarSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] gap-0 overflow-y-auto p-0 sm:max-w-[560px]">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="font-display text-lg font-bold">Edit profile</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4 px-6 py-5">
          <div className="flex items-center gap-4 rounded-xl border border-border bg-secondary/50 p-4">
            <Avatar
              initials={identity?.initials ?? "?"}
              name={identity?.name}
              src={identity?.avatarUrl}
              size={64}
              me
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground">Profile picture</div>
              <div className="mt-0.5 text-xs text-ink-3">JPEG, PNG, or WebP · up to 5 MB</div>
              {avatarError && <p role="alert" className="mt-1.5 text-xs font-medium text-primary">{avatarError}</p>}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={uploadAvatar}
              className="sr-only"
              aria-label="Choose profile picture"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={avatarSaving}
              onClick={() => fileInputRef.current?.click()}
            >
              <Camera className="size-3.5" />
              {identity?.avatarUrl ? "Replace" : "Upload"}
            </Button>
            {identity?.avatarUrl && (
              <Button type="button" variant="ghost" size="sm" disabled={avatarSaving} onClick={removeAvatar}>
                <Trash2 className="size-3.5" />
                Remove
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="firstName" label="First name" value={form.firstName} onChange={set} />
            <Field id="lastName" label="Last name" value={form.lastName} onChange={set} />
          </div>

          {/* Read-only context — never part of the write path (D4). */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Username</Label>
              <Input value={identity?.username ? `@${identity.username}` : "Not set"} disabled aria-label="Username (read only)" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Email</Label>
              <Input value={identity?.email ?? ""} disabled aria-label="Email (read only)" />
            </div>
          </div>

          <Field id="phoneNumber" label="Phone" value={form.phoneNumber} onChange={set} />
          <Field id="companyName" label="Company" value={form.companyName} onChange={set} />

          <Field id="address1" label="Address" value={form.address1} onChange={set} />
          <Field id="address2" label="Address line 2" value={form.address2} onChange={set} />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="city" label="City" value={form.city} onChange={set} />
            <Field id="region" label="State / Province" value={form.region} onChange={set} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="postalCode" label="Postal code" value={form.postalCode} onChange={set} />
            <Field id="country" label="Country" value={form.country} onChange={set} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field id="pronouns" label="Pronouns" value={form.pronouns} onChange={set} />
            <div className="flex flex-col justify-end gap-1.5 pb-1">
              <Label>Time zone</Label>
              <Link href="/settings" className="text-sm font-semibold text-primary underline underline-offset-2">
                Change it in Settings
              </Link>
            </div>
          </div>

          <DialogFooter className="mt-1 gap-2.5">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
